import React from "react";
import { Typography, Box, Skeleton, useTheme } from "@mui/material";
import heroImage from "../../../assets/images/heroImg.webp";
import overlayImage from "../../../assets/images/overlayImg.webp";
import CustomButton from "../../shared/CustomButton";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useNavigation from "../../../hooks/useNavigation";

const HeroSection = ({ data }) => {
  const theme = useTheme();
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;
  const { navigateTo } = useNavigation();

  const handleContactUS = () => {
    navigateTo(`/${currentLang}/contact`);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <>
      {/* Preload link for hero image */}
      <link rel="preload" as="image" href={heroImage} />

      <Box
        sx={{
          position: "relative",
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
        }}
      >
        {/* Overlay Background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#061332c9",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            padding: "48px 24px",
            borderRadius: "8px",
            width: { xs: "100%", md: "60%" },
            zIndex: 10,
          }}
        >
          {/* Title with Skeleton */}
          {data?.title[currentLang] ? (
            <Typography
              sx={{
                fontSize: {
                  xs: "32px",
                  sm: "48px",
                  md: "56px",
                  lg: "64px",
                },
                fontWeight: 700,
                color: theme.palette.primary.negative,
                lineHeight: {
                  xs: "40px",
                  sm: "55px",
                  md: "65px",
                  lg: "70.4px",
                },
                mb: 4,
              }}
            >
              {data.title[currentLang]}
            </Typography>
          ) : (
            <Skeleton
              variant="text"
              sx={{
                fontSize: {
                  xs: "32px",
                  sm: "48px",
                  md: "56px",
                  lg: "64px",
                },
                mb: 4,
                backgroundColor: theme.palette.grey[800],
              }}
            />
          )}

          {/* Description with Skeleton */}
          {data?.description[currentLang] ? (
            <Typography
              sx={{
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "20px",
                },
                fontWeight: 500,
                color: theme.palette.primary.negative,
                lineHeight: {
                  xs: "24px",
                  sm: "28px",
                  md: "30px",
                },
                mb: 8,
              }}
            >
              {data.description[currentLang]}
            </Typography>
          ) : (
            <Skeleton
              variant="text"
              sx={{
                fontSize: {
                  xs: "16px",
                  sm: "18px",
                  md: "20px",
                },
                mb: 8,
                backgroundColor: theme.palette.grey[800],
              }}
            />
          )}

          {/* Buttons */}
          <Box display="flex" gap={2} justifyContent="center">
            <CustomButton
              backgroundColor="transparent"
              border
              onClick={() => scrollToSection("targetSectionId")}
            >
              {t("buttons.Learn more")}
              <KeyboardArrowDown
                sx={{
                  ml: currentLang === "ar" ? 0 : 1,
                  mr: currentLang === "ar" ? 1 : 0,
                  color: "white",
                }}
              />
            </CustomButton>
            <CustomButton
              backgroundColor="#E0B91B"
              textColor="#0A1F52"
              onClick={handleContactUS}
            >
              {t("buttons.Get a Consultation")}
            </CustomButton>
          </Box>
        </Box>

        {/* Overlay Image */}
        <Box
          component="img"
          src={overlayImage}
          alt={t("alt.overlayImage")}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 3,
            aspectRatio: "16 / 9",
          }}
          loading="lazy"
        />
      </Box>
    </>
  );
};

export default React.memo(HeroSection);
